/**
 * Uma ordem de serviço: situação e próximos passos, dados, serviços e peças,
 * ocorrências vinculadas e linha do tempo.
 *
 * Serve às duas experiências, como a execução do checklist: SIAG web
 * (`variant="web"`) e Portal Operacional (`variant="portal"`). O layout é de uma
 * coluna no celular e duas na tela larga.
 *
 * Concluir pede o laudo e cancelar pede o motivo ANTES de chamar a API — a API
 * recusaria de qualquer forma, mas o texto é o que a oficina precisa escrever.
 */
import { WorkOrderTires } from "../features/tires/WorkOrderTires";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenanceCriticalityDto, MaintenanceNonConformityDto, MaintenancePlanPlateDto, MaintenancePlanRowDto,
  MaintenanceWorkOrderDetailDto, MaintenanceWorkOrderEventDto,
} from "@bi/types";
import { PLAN_STATUS, intervalText } from "../features/maintenance/preventive-ui";
import { api, ApiError } from "../lib/api";
import { fmtBRL, fmtNumber } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { CriticalityBadge, NC_STATUS, WO_STATUS } from "../features/maintenance/labels";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { portalPaths } from "../features/portal/portal-nav";
import {
  fromDateInput, isWorkOrderClosed, parseBRNumber, toDateInput, workOrderActions, type WorkOrderAction,
} from "../features/maintenance/work-order-ui";
import { EvidenceThumb, EvidenceThumbs } from "../features/maintenance/EvidenceThumbs";
import { WorkOrderChecklist } from "../features/maintenance/WorkOrderChecklist";
import { WorkOrderPlanCard } from "../features/maintenance/WorkOrderPlanCard";
import { WorkOrderErpLink } from "../features/maintenance/WorkOrderErpLink";
import { WorkOrderServicesTable } from "../features/maintenance/WorkOrderServicesTable";
import { TechnicalItemPanel } from "../features/maintenance/TechnicalItemPanel";
import { MaintenanceIcon } from "../features/maintenance/MaintenanceIcons";
import { COND_LABEL, COND_STYLE, POST_CONDITION, PROBLEM_STATE } from "../features/maintenance/work-order-inspection-ui";

const TYPE_LABEL = { preventive: "Preventiva", corrective: "Corretiva" } as const;
const fmtDT = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

export function MaintenanceWorkOrderPage({ variant = "web" }: { variant?: "web" | "portal" }) {
  const { id = "" } = useParams();
  const portal = variant === "portal";
  const qc = useQueryClient();
  const { can } = useCapabilities();
  const [erro, setErro] = useState<string | null>(null);
  // item técnico aberto: a execução abre em tela cheia dentro da O.S. (cartões, eixos ou posições)
  const [tecnico, setTecnico] = useState<string | null>(null);
  const [notas, setNotas] = useState<string | null>(null);

  const { data: o, isLoading } = useQuery({
    queryKey: ["maintenance", "work-order", id],
    queryFn: () => api.get<MaintenanceWorkOrderDetailDto>(`/maintenance/work-orders/${id}`),
  });

  const atualizar = () => {
    setErro(null);
    qc.invalidateQueries({ queryKey: ["maintenance"] });
  };
  const falha = (msg: string) => (e: unknown) => setErro(e instanceof ApiError ? e.message : msg);

  // Itens do plano preventivo da placa: a conclusão registra quais foram feitos.
  const plano = useQuery({
    queryKey: ["maintenance", "preventive", "plate", o?.vehiclePlate],
    queryFn: () => api.get<MaintenancePlanPlateDto>(`/maintenance/preventive/plates/${encodeURIComponent(o!.vehiclePlate)}`),
    enabled: !!o && !isWorkOrderClosed(o.status) && can("workorder.manage"),
  });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/maintenance/work-orders/${id}`, body),
    onSuccess: atualizar,
    onError: falha("Não foi possível salvar."),
  });

  if (isLoading || !o) return <div className="p-8 text-sm text-gray-500">Carregando ordem de serviço…</div>;

  const gerencia = can("workorder.manage");
  const fechada = isWorkOrderClosed(o.status);
  const voltar = portal ? portalPaths.maintenance : "/frota/manutencao/ordens";
  const linkChecklist = (runId: string) => (portal ? portalPaths.run(runId) : `/frota/manutencao/checklists/${runId}`);

  return (
    <div className={`space-y-4 ${portal ? "pb-20" : ""}`}>
      <PageHeader
        title="Ordem de Serviço"
        subtitle={o.type === "preventive"
          ? "Execução de manutenção preventiva vinculada ao veículo e checklist do grupo."
          : `${TYPE_LABEL[o.type]} · aberta em ${fmtDT(o.openedAt)}${o.createdByName ? ` por ${o.createdByName}` : ""}`}
        badge={
          <span className="flex gap-1 flex-wrap">
            {o.overdue && <span className="badge-red">Atrasada</span>}
            {o.vehicleStopped && <span className="badge-red">Veículo parado</span>}
          </span>
        }
        actions={
          <span className="flex gap-2">
            {/* Nova aba: a folha abre a janela de impressão sozinha. */}
            <a className="btn-secondary" href={`/frota/manutencao/ordens/${o.id}/imprimir`} target="_blank" rel="noreferrer">Imprimir</a>
            <Link className="btn-secondary" to={voltar}>Voltar</Link>
          </span>
        }
      />

      {erro && <div className="card p-3 border-danger-600/30 bg-danger-50 text-sm text-danger-600">{erro}</div>}

      <CabecalhoOS o={o} />

      {fechada && (
        <div className={`card p-4 text-sm ${o.status === "completed" ? "bg-tm-50 border-tm-100" : "bg-surface-muted"}`}>
          <p className="font-medium text-gray-900">
            {o.status === "completed" ? "Concluída" : "Cancelada"} em {fmtDT(o.closedAt)}
            {o.closedByName ? ` por ${o.closedByName}` : ""}
          </p>
          <p className="text-gray-700 mt-1 whitespace-pre-line">
            {o.status === "completed" ? `Laudo: ${o.resolution ?? "—"}` : `Motivo: ${o.cancelReason ?? "—"}`}
          </p>
        </div>
      )}

      {tecnico ? (
        <TechnicalItemPanel
          key={tecnico} workOrderId={o.id} itemId={tecnico} podeEditar={can("workorder.inspect")}
          responsavel={o.assignee} onFechar={() => setTecnico(null)} onMudou={atualizar}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <WorkOrderChecklist
              o={o} editavel={can("workorder.checklist") && !fechada} podeInspecionar={can("workorder.inspect")}
              tecnicoAberto={tecnico} onTecnico={setTecnico} onMudou={atualizar}
            />
            <WorkOrderServicesTable o={o} onAbrirTecnico={setTecnico} />
            <ProblemasEncontrados o={o} />
            <ResumoTecnicoFinal o={o} />
            <WorkOrderPlanCard o={o} />
            <div className="card p-4">
              <h3 className="font-medium text-gray-900 mb-1">Serviço</h3>
              <p className="text-sm text-gray-700 whitespace-pre-line">{o.description}</p>
            </div>
            <Itens o={o} editavel={gerencia && !fechada} onMudou={atualizar} onErro={falha("Não foi possível alterar os itens.")} />
            <Ocorrencias o={o} editavel={gerencia && !fechada} linkChecklist={linkChecklist}
              onMudou={atualizar} onErro={falha("Não foi possível alterar as ocorrências.")} />
            <WorkOrderTires o={o} fechada={fechada} />
          </div>
          <div className="space-y-4">
            <ResumoOS o={o} />
            <Observacoes
              valor={notas ?? o.notes ?? ""} editavel={gerencia && !fechada} salvando={patch.isPending}
              onMudar={setNotas} onSalvar={() => patch.mutate({ notes: (notas ?? o.notes ?? "").trim() || null }, { onSuccess: () => setNotas(null) })}
              alterado={notas !== null && notas !== (o.notes ?? "")}
            />
            <Evidencias o={o} />
            {gerencia && !fechada && (
              <section className="card p-4 space-y-3">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><MaintenanceIcon name="wrench" className="h-5 w-5 text-tm-700" />Ações</h3>
                <ProximosPassos
                  acoes={workOrderActions(o.status)}
                  pendente={patch.isPending}
                  plano={plano.data?.rows ?? []}
                  planoDaOS={o.planComponents ?? []}
                  checklist={o.checklist}
                  problemasAbertos={o.technical?.summary.open ?? 0}
                  onAcao={(a, texto, executados, justificativa) =>
                    patch.mutate({
                      status: a.to,
                      ...(a.requires === "resolution" ? { resolution: texto } : {}),
                      ...(a.requires === "cancelReason" ? { cancelReason: texto } : {}),
                      ...(executados?.length ? { executedPlanComponents: executados } : {}),
                      ...(justificativa ? { pendingJustification: justificativa } : {}),
                    })
                  }
                />
              </section>
            )}
            <Dados o={o} editavel={gerencia && !fechada} salvando={patch.isPending} onSalvar={(b) => patch.mutate(b)} />
            <WorkOrderErpLink o={o} pode={can("workorder.erp_link")} onMudou={atualizar} />
            <Historico o={o} podeComentar={can("checklist.execute")} onMudou={atualizar} onErro={falha("Não foi possível comentar.")} />
          </div>
        </div>
      )}
    </div>
  );
}

function ProximosPassos({
  acoes, pendente, plano, planoDaOS, checklist, problemasAbertos, onAcao,
}: {
  acoes: WorkOrderAction[];
  pendente: boolean;
  plano: MaintenancePlanRowDto[];
  planoDaOS: string[];
  checklist: MaintenanceWorkOrderDetailDto["checklist"];
  problemasAbertos: number;
  onAcao: (a: WorkOrderAction, texto?: string, executados?: string[], justificativa?: string) => void;
}) {
  const [pedindo, setPedindo] = useState<WorkOrderAction | null>(null);
  const [texto, setTexto] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const resumo = checklist.summary;
  const obrigatorios = resumo.requiredPending;
  const pendentes = resumo.pending;
  const [executados, setExecutados] = useState<Set<string>>(() => new Set(planoDaOS));
  useEffect(() => { setExecutados(new Set(planoDaOS)); }, [planoDaOS.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (pedindo) {
    const laudo = pedindo.requires === "resolution";
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-900">{laudo ? "Laudo: o que foi feito?" : "Motivo do cancelamento"}</p>
        <textarea className="input w-full" rows={3} autoFocus value={texto} maxLength={laudo ? 4000 : 1000}
          placeholder={laudo ? "Ex.: Trocadas as duas lâmpadas H7 do farol esquerdo, testado." : "Ex.: Aberta em duplicidade."}
          onChange={(e) => setTexto(e.target.value)} />
        {laudo && checklist.items.length > 0 && (
          <div className="rounded-lg border border-surface-border bg-surface-muted p-3 text-sm">
            <p className="font-medium text-gray-900">Checklist preventivo</p>
            <p className="text-gray-700">
              {resumo.total} itens previstos · {resumo.done} realizados · {resumo.notDone} não realizado{resumo.notDone === 1 ? "" : "s"} · {resumo.notApplicable} não se aplica · {pendentes} pendente{pendentes === 1 ? "" : "s"}
            </p>
            {obrigatorios > 0 && (
              <p className="mt-1 text-danger-600">Há {obrigatorios} item(ns) obrigatório(s) pendente(s). Marque-os no checklist antes de concluir.</p>
            )}
            {problemasAbertos > 0 && (
              <p className="mt-1 text-danger-600">Há {problemasAbertos} componente(s) com problema sem destino. Registre a intervenção ou deixe como pendente, com motivo.</p>
            )}
            {obrigatorios === 0 && pendentes > 0 && (
              <textarea className="input mt-2 w-full" rows={2} maxLength={1000} value={justificativa} onChange={(e) => setJustificativa(e.target.value)}
                placeholder="Justificativa para concluir com itens pendentes (obrigatória)" />
            )}
          </div>
        )}
        {laudo && <p className="text-xs text-gray-500">As ocorrências vinculadas serão resolvidas com este laudo.</p>}
        {laudo && plano.length > 0 && (
          <fieldset className="border border-surface-border rounded-lg p-3">
            <legend className="text-xs text-gray-500 px-1">Itens do plano preventivo feitos nesta O.S.</legend>
            <div className="grid sm:grid-cols-2 gap-1">
              {plano.map((r) => (
                <label key={r.componentCode} className="flex items-start gap-2 text-sm text-gray-700 py-1">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={executados.has(r.componentCode)}
                    onChange={(e) => setExecutados((s) => { const n = new Set(s); if (e.target.checked) n.add(r.componentCode); else n.delete(r.componentCode); return n; })}
                  />
                  <span>
                    {r.label}
                    <span className="block text-xs text-gray-400">{PLAN_STATUS[r.status.status].label} · {intervalText(r)}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Cada item marcado passa a contar a partir de hoje, com o KM da O.S.</p>
          </fieldset>
        )}
        {!laudo && <p className="text-xs text-gray-500">As ocorrências vinculadas voltam para a fila, sem O.S.</p>}
        <div className="flex gap-2">
          <button className="btn-secondary" disabled={pendente} onClick={() => { setPedindo(null); setTexto(""); setJustificativa(""); }}>Voltar</button>
          <button
            className={pedindo.tone === "danger" ? "btn-secondary border-danger-600/40 text-danger-600" : "btn-primary"}
            disabled={pendente || texto.trim() === "" || (laudo && (obrigatorios > 0 || problemasAbertos > 0 || (pendentes > 0 && justificativa.trim() === "")))}
            onClick={() => onAcao(pedindo, texto.trim(), laudo ? [...executados] : undefined, laudo && pendentes > 0 ? justificativa.trim() : undefined)}
          >
            {pendente ? "Salvando…" : pedindo.label}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {acoes.map((a) => (
        <button
          key={a.to}
          disabled={pendente}
          onClick={() => (a.requires ? setPedindo(a) : onAcao(a))}
          className={`min-h-[44px] ${
            a.tone === "primary" ? "btn-primary" : a.tone === "danger" ? "btn-ghost text-danger-600 sm:ml-auto" : "btn-secondary"
          }`}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

function Dados({
  o, editavel, salvando, onSalvar,
}: { o: MaintenanceWorkOrderDetailDto; editavel: boolean; salvando: boolean; onSalvar: (b: Record<string, unknown>) => void }) {
  const [editando, setEditando] = useState(false);
  const [f, setF] = useState(() => formDe(o));
  useEffect(() => { if (!editando) setF(formDe(o)); }, [o, editando]);

  if (editando) {
    return (
      <div className="card p-4 space-y-3">
        <h3 className="font-medium text-gray-900">Dados da O.S.</h3>
        <Campo rotulo="Prioridade">
          <select className="input w-full" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value as MaintenanceCriticalityDto })}>
            <option value="high">Alta</option><option value="medium">Média</option><option value="low">Baixa</option>
          </select>
        </Campo>
        <Campo rotulo="Tipo">
          <select className="input w-full" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value as "preventive" | "corrective" })}>
            <option value="corrective">Corretiva</option><option value="preventive">Preventiva</option>
          </select>
        </Campo>
        <Campo rotulo="Executor">
          <select className="input w-full" value={f.executor} onChange={(e) => setF({ ...f, executor: e.target.value as "internal" | "external" })}>
            <option value="internal">Oficina própria</option><option value="external">Terceiro</option>
          </select>
        </Campo>
        {f.executor === "external" && (
          <Campo rotulo="Oficina / fornecedor">
            <input className="input w-full" value={f.supplierName} maxLength={200} onChange={(e) => setF({ ...f, supplierName: e.target.value })} />
          </Campo>
        )}
        <Campo rotulo="Responsável">
          <input className="input w-full" value={f.assignee} maxLength={200} onChange={(e) => setF({ ...f, assignee: e.target.value })} />
        </Campo>
        <Campo rotulo="Prazo">
          <input type="date" className="input w-full" value={f.dueAt} onChange={(e) => setF({ ...f, dueAt: e.target.value })} />
        </Campo>
        <Campo rotulo="KM na abertura">
          <input className="input w-full" inputMode="numeric" value={f.odometerKm}
            onChange={(e) => setF({ ...f, odometerKm: e.target.value.replace(/\D/g, "") })} />
        </Campo>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={f.vehicleStopped} onChange={(e) => setF({ ...f, vehicleStopped: e.target.checked })} />
          Veículo parado aguardando o serviço
        </label>
        <Campo rotulo="Observações">
          <textarea className="input w-full" rows={2} value={f.notes} maxLength={2000} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </Campo>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setEditando(false)}>Cancelar</button>
          <button
            className="btn-primary"
            disabled={salvando || (f.executor === "external" && f.supplierName.trim() === "")}
            onClick={() => {
              onSalvar({
                priority: f.priority,
                type: f.type,
                executor: f.executor,
                supplierName: f.executor === "external" ? f.supplierName.trim() : null,
                assignee: f.assignee.trim() || null,
                dueAt: f.dueAt ? fromDateInput(f.dueAt) : null,
                odometerKm: f.odometerKm ? Number(f.odometerKm) : null,
                vehicleStopped: f.vehicleStopped,
                notes: f.notes.trim() || null,
              });
              setEditando(false);
            }}
          >
            Salvar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-gray-900">Dados da O.S.</h3>
        {editavel && <button className="btn-ghost text-sm" onClick={() => setEditando(true)}>Editar</button>}
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
        <Item rotulo="Prioridade"><CriticalityBadge value={o.priority} /></Item>
        <Item rotulo="Tipo">{TYPE_LABEL[o.type]}</Item>
        <Item rotulo="Executor">{o.executor === "external" ? "Terceiro" : "Oficina própria"}</Item>
        <Item rotulo={o.executor === "external" ? "Fornecedor" : "Responsável"}>
          {(o.executor === "external" ? o.supplierName : o.assignee) ?? "—"}
        </Item>
        <Item rotulo="Prazo">
          <span className={o.overdue ? "text-danger-600 font-medium" : ""}>{o.dueAt ? new Date(o.dueAt).toLocaleDateString("pt-BR") : "—"}</span>
        </Item>
        <Item rotulo="KM">{o.odometerKm != null ? fmtNumber(o.odometerKm) : "—"}</Item>
        <Item rotulo="Início da execução">{fmtDT(o.startedAt)}</Item>
        <Item rotulo="Implemento"><span className="font-mono">{o.trailerPlate ?? "—"}</span></Item>
        {o.executor === "external" && o.assignee && <Item rotulo="Responsável">{o.assignee}</Item>}
      </dl>
      {o.notes && <p className="mt-3 text-sm text-gray-600 whitespace-pre-line border-t border-gray-100 pt-2">{o.notes}</p>}
    </div>
  );
}

function formDe(o: MaintenanceWorkOrderDetailDto) {
  return {
    priority: o.priority,
    type: o.type,
    executor: o.executor,
    supplierName: o.supplierName ?? "",
    assignee: o.assignee ?? "",
    dueAt: toDateInput(o.dueAt),
    odometerKm: o.odometerKm != null ? String(o.odometerKm) : "",
    vehicleStopped: o.vehicleStopped,
    notes: o.notes ?? "",
  };
}

function Itens({
  o, editavel, onMudou, onErro,
}: { o: MaintenanceWorkOrderDetailDto; editavel: boolean; onMudou: () => void; onErro: (e: unknown) => void }) {
  const [kind, setKind] = useState<"service" | "part">("part");
  const [descricao, setDescricao] = useState("");
  const [qtd, setQtd] = useState("1");
  const [unit, setUnit] = useState("");
  const [custoManual, setCustoManual] = useState("");

  const qtdN = parseBRNumber(qtd) ?? 0;
  const unitN = parseBRNumber(unit);

  const adicionar = useMutation({
    mutationFn: () => api.post(`/maintenance/work-orders/${o.id}/items`, { kind, description: descricao.trim(), quantity: qtdN, unitCost: unitN }),
    onSuccess: () => { setDescricao(""); setQtd("1"); setUnit(""); onMudou(); },
    onError: onErro,
  });
  const remover = useMutation({
    mutationFn: (itemId: string) => api.del(`/maintenance/work-orders/${o.id}/items/${itemId}`),
    onSuccess: onMudou,
    onError: onErro,
  });
  const salvarCusto = useMutation({
    mutationFn: () => api.patch(`/maintenance/work-orders/${o.id}`, { cost: parseBRNumber(custoManual) }),
    onSuccess: () => { setCustoManual(""); onMudou(); },
    onError: onErro,
  });

  const podeAdicionar = descricao.trim() !== "" && qtdN > 0 && unitN != null && unitN >= 0;

  return (
    <div className="card">
      <div className="px-4 pt-4 pb-2 flex items-baseline justify-between">
        <h3 className="font-medium text-gray-900">Serviços e peças</h3>
        <span className="text-sm text-gray-500">
          Total <strong className="text-gray-900">{o.cost != null ? fmtBRL(Number(o.cost)) : "—"}</strong>
        </span>
      </div>
      {o.items.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase bg-surface-muted">
              <tr>
                <th className="text-left px-4 py-2">Item</th>
                <th className="text-right px-4 py-2">Qtd</th>
                <th className="text-right px-4 py-2 hidden sm:table-cell">Unitário</th>
                <th className="text-right px-4 py-2">Total</th>
                {editavel && <th />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {o.items.map((i) => (
                <tr key={i.id}>
                  <td className="px-4 py-2">
                    <span className={i.kind === "part" ? "badge-gray" : "badge-blue"}>{i.kind === "part" ? "Peça" : "Serviço"}</span>
                    <span className="block sm:inline sm:ml-2 text-gray-900">{i.description}</span>
                  </td>
                  <td className="px-4 py-2 text-right">{Number(i.quantity).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-2 text-right hidden sm:table-cell">{fmtBRL(Number(i.unitCost))}</td>
                  <td className="px-4 py-2 text-right text-gray-900">{fmtBRL(Number(i.totalCost))}</td>
                  {editavel && (
                    <td className="px-2 py-2 text-right">
                      <button className="btn-ghost text-xs text-danger-600" disabled={remover.isPending} onClick={() => remover.mutate(i.id)}>Remover</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="text-xs text-gray-500">
              <tr>
                <td className="px-4 py-2" colSpan={editavel ? 5 : 4}>
                  Peças {fmtBRL(o.totals.parts)} · Serviços {fmtBRL(o.totals.services)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <p className="px-4 pb-3 text-sm text-gray-500">
          Nenhum item lançado.{o.cost != null ? " O custo acima foi informado à mão." : ""}
        </p>
      )}

      {editavel && (
        <div className="border-t border-gray-100 p-4 space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-12 gap-2">
            <select className="input sm:col-span-2" value={kind} onChange={(e) => setKind(e.target.value as "service" | "part")}>
              <option value="part">Peça</option>
              <option value="service">Serviço</option>
            </select>
            <input className="input col-span-2 sm:col-span-5" placeholder={kind === "part" ? "Ex.: Lâmpada H7" : "Ex.: Mão de obra elétrica"}
              value={descricao} maxLength={500} onChange={(e) => setDescricao(e.target.value)} />
            <input className="input sm:col-span-1" inputMode="decimal" placeholder="Qtd" value={qtd} onChange={(e) => setQtd(e.target.value)} />
            <input className="input sm:col-span-2" inputMode="decimal" placeholder="R$ unitário" value={unit} onChange={(e) => setUnit(e.target.value)} />
            <button className="btn-primary sm:col-span-2 justify-center" disabled={!podeAdicionar || adicionar.isPending} onClick={() => adicionar.mutate()}>
              Adicionar
            </button>
          </div>
          {o.items.length === 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
              <span>Ou informe só o custo total:</span>
              <input className="input w-36" inputMode="decimal" placeholder="R$" value={custoManual} onChange={(e) => setCustoManual(e.target.value)} />
              <button className="btn-secondary" disabled={parseBRNumber(custoManual) == null || salvarCusto.isPending} onClick={() => salvarCusto.mutate()}>Salvar custo</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Ocorrencias({
  o, editavel, linkChecklist, onMudou, onErro,
}: {
  o: MaintenanceWorkOrderDetailDto;
  editavel: boolean;
  linkChecklist: (runId: string) => string;
  onMudou: () => void;
  onErro: (e: unknown) => void;
}) {
  const disponiveis = useQuery({
    queryKey: ["maintenance", "ncs", "livres", o.vehiclePlate],
    queryFn: () => api.get<{ rows: MaintenanceNonConformityDto[] }>(`/maintenance/non-conformities?plate=${o.vehiclePlate}&limit=100`),
    enabled: editavel,
    select: (d) => d.rows.filter((n) => !n.workOrderId && (n.status === "open" || n.status === "in_analysis")),
  });
  const vincular = useMutation({
    mutationFn: (ncId: string) => api.post(`/maintenance/work-orders/${o.id}/non-conformities`, { nonConformityId: ncId }),
    onSuccess: onMudou,
    onError: onErro,
  });
  const desvincular = useMutation({
    mutationFn: (ncId: string) => api.del(`/maintenance/work-orders/${o.id}/non-conformities/${ncId}`),
    onSuccess: onMudou,
    onError: onErro,
  });

  return (
    <div className="card">
      <h3 className="px-4 pt-4 pb-2 font-medium text-gray-900">Ocorrências vinculadas</h3>
      {o.nonConformities.length === 0 ? (
        <p className="px-4 pb-3 text-sm text-gray-500">
          {o.origin === "preventive" ? "O.S. do plano preventivo — sem ocorrência de checklist." : "O.S. avulsa — nenhuma ocorrência de checklist."}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {o.nonConformities.map((n) => (
            <li key={n.id} className="px-4 py-3 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900">{n.itemLabel}{n.defectLabel ? ` — ${n.defectLabel}` : ""}</p>
                {n.description && <p className="text-xs text-gray-500">{n.description}</p>}
                {n.tire && (
                  <p className="text-xs text-gray-700 mt-0.5">
                    Pneu <Link className="font-mono text-tm-700 hover:underline" to={`/frota/pneus/${n.tire.id}`}>{n.tire.code}</Link>
                    {n.tirePosition ? ` · ${n.tirePosition.label}` : ""} · {o.vehiclePlate}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(n.openedAt).toLocaleDateString("pt-BR")}
                  {n.driverName ? ` · ${n.driverName}` : ""}
                  {" · "}
                  <Link className="text-tm-600 hover:underline" to={linkChecklist(n.runId)}>ver checklist</Link>
                </p>
                {/* A foto do problema, sem precisar voltar ao checklist. */}
                <EvidenceThumbs anexos={n.attachments} size="sm" />
              </div>
              <CriticalityBadge value={n.criticality} />
              <span className={NC_STATUS[n.status].badge}>{NC_STATUS[n.status].label}</span>
              {editavel && (
                <button className="btn-ghost text-xs text-gray-500" disabled={desvincular.isPending} onClick={() => desvincular.mutate(n.id)}>
                  Desvincular
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editavel && (disponiveis.data?.length ?? 0) > 0 && (
        <div className="border-t border-gray-100 p-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500">Outras ocorrências abertas de {o.vehiclePlate}:</span>
          <select className="input flex-1 min-w-[200px]" defaultValue="" onChange={(e) => { if (e.target.value) vincular.mutate(e.target.value); e.target.value = ""; }}>
            <option value="">Vincular a esta O.S.…</option>
            {disponiveis.data!.map((n) => <option key={n.id} value={n.id}>{n.itemLabel}{n.description ? ` — ${n.description}` : ""}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

const EVENTO_TITULO: Record<MaintenanceWorkOrderEventDto["type"], string> = {
  created: "O.S. aberta",
  status: "Situação",
  comment: "Comentário",
  item_added: "Item lançado",
  item_removed: "Item removido",
  nc_linked: "Ocorrência vinculada",
  nc_unlinked: "Ocorrência desvinculada",
  updated: "Dados alterados",
};

function Historico({
  o, podeComentar, onMudou, onErro,
}: { o: MaintenanceWorkOrderDetailDto; podeComentar: boolean; onMudou: () => void; onErro: (e: unknown) => void }) {
  const [texto, setTexto] = useState("");
  const comentar = useMutation({
    mutationFn: () => api.post(`/maintenance/work-orders/${o.id}/comments`, { message: texto.trim() }),
    onSuccess: () => { setTexto(""); onMudou(); },
    onError: onErro,
  });

  return (
    <div className="card p-4">
      <h3 className="font-medium text-gray-900 mb-3">Histórico</h3>
      <ol className="relative border-l border-gray-200 ml-1.5 space-y-3">
        {[...o.events].reverse().map((e) => (
          <li key={e.id} className="ml-4">
            <span className={`absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full ${e.type === "comment" ? "bg-tm-500" : "bg-gray-300"}`} />
            <p className="text-xs text-gray-400">{fmtDT(e.createdAt)}{e.createdByName ? ` · ${e.createdByName}` : ""}</p>
            <p className="text-sm text-gray-900">
              {e.type === "status" && e.toStatus
                ? `${e.fromStatus ? `${WO_STATUS[e.fromStatus].label} → ` : ""}${WO_STATUS[e.toStatus].label}`
                : EVENTO_TITULO[e.type]}
            </p>
            {e.message && <p className="text-sm text-gray-600 whitespace-pre-line">{e.message}</p>}
          </li>
        ))}
      </ol>
      {podeComentar && (
        <div className="mt-4 space-y-2">
          <textarea className="input w-full" rows={2} placeholder="Comentar (ex.: peça chega quinta)" value={texto} maxLength={2000}
            onChange={(e) => setTexto(e.target.value)} />
          <button className="btn-secondary" disabled={texto.trim() === "" || comentar.isPending} onClick={() => comentar.mutate()}>
            Comentar
          </button>
        </div>
      )}
    </div>
  );
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-gray-500">{rotulo}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function Item({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{rotulo}</dt>
      <dd className="text-gray-900">{children}</dd>
    </div>
  );
}


/** Cabeçalho da O.S. como na referência: uma faixa com Nº, placa, veículo, grupo, plano, KM, abertura, responsável e oficina. */
function CabecalhoOS({ o }: { o: MaintenanceWorkOrderDetailDto }) {
  const preventiva = o.type === "preventive";
  const plano = o.planSnapshot?.components.map((c) => c.label) ?? [];
  const modelo = o.vehicle?.model ?? null;
  const marca = o.vehicle?.brand ?? null;
  const aberta = new Date(o.openedAt);
  const Col = ({ r, children, sub }: { r: string; children: React.ReactNode; sub?: string | null }) => (
    <div className="min-w-0 px-4 first:pl-0">
      <dt className="text-xs text-gray-500">{r}</dt>
      <dd className="font-semibold text-gray-900">{children}</dd>
      {sub && <p className="text-xs font-normal text-gray-400">{sub}</p>}
    </div>
  );
  return (
    <section className="card p-4 space-y-3">
      <dl className="grid grid-cols-2 gap-y-3 sm:grid-cols-3 lg:grid-cols-[1.4fr_1fr_1.2fr_1.2fr_1.4fr_1fr_1fr_1fr_1.1fr] lg:divide-x lg:divide-surface-border">
        <Col r="Nº da OS">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-xl font-bold">OS-{String(o.number).padStart(6, "0")}</span>
            <span className={WO_STATUS[o.status].badge}>{WO_STATUS[o.status].label}</span>
          </span>
        </Col>
        <Col r="Placa"><span className="font-mono text-lg">{o.vehiclePlate}</span></Col>
        <Col r="Veículo" sub={marca && modelo ? marca : o.vehicle ? (o.vehicle.isTrailer ? "Carreta" : "Cavalo") : null}>{modelo ?? marca ?? "—"}</Col>
        <Col r="Grupo de veículos">{o.vehicle?.group?.name ?? "—"}</Col>
        <Col r="Plano preventivo">{preventiva ? (plano.length ? plano.join(", ") : o.planComponents.join(", ") || "—") : "—"}</Col>
        <Col r="Quilometragem atual">{o.odometerKm != null ? `${fmtNumber(o.odometerKm)} km` : "—"}</Col>
        <Col r="Data de abertura" sub={aberta.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}>{aberta.toLocaleDateString("pt-BR")}</Col>
        <Col r="Responsável" sub={o.executor === "external" ? "Terceiro" : "Mecânico"}>{o.assignee ?? "—"}</Col>
        <Col r="Oficina / Equipe" sub={o.vehicleStopped ? "Veículo parado" : null}>{o.executor === "external" ? o.supplierName ?? "Terceiro" : "Oficina Própria"}</Col>
      </dl>
      {preventiva && o.checklist.bound && (
        <div className="flex items-start gap-3 rounded-lg bg-tm-50 px-4 py-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tm-600 text-white" aria-hidden>✓</span>
          <div className="text-sm">
            <p className="font-semibold text-tm-800">Checklist identificado automaticamente pelo grupo do veículo via placa.</p>
            <p className="text-gray-600">
              Ao abrir a OS pela placa, o sistema localizou o checklist do plano preventivo vinculado{o.vehicle?.group ? ` ao grupo ${o.vehicle.group.name}` : ""}.
            </p>
          </div>
        </div>
      )}
      {preventiva && !o.checklist.bound && o.planComponents.length > 0 && (
        <p className="rounded-lg border border-warning-400/40 bg-warning-50 px-3 py-2 text-sm text-warning-700">
          Nenhum checklist vinculado para este plano/veículo. A O.S. segue normalmente, pelos itens e serviços lançados.
        </p>
      )}
    </section>
  );
}

/** "Resumo da OS": quatro números do checklist e a previsão de conclusão, como na referência. */
function ResumoOS({ o }: { o: MaintenanceWorkOrderDetailDto }) {
  const r = o.checklist.summary;
  const t = o.technical;
  const pc = o.technicalReport?.postCondition ?? t?.postCondition ?? null;
  if (!r.total) return null;
  const validados = r.total - r.pending;
  const pct = (n: number) => `${(Math.round((n / r.total) * 1000) / 10).toString().replace(".", ",")}%`;
  const dias = o.dueAt ? Math.ceil((new Date(o.dueAt).getTime() - Date.now()) / 86_400_000) : null;
  return (
    <section className="card p-4">
      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900"><MaintenanceIcon name="suspension" className="h-5 w-5 text-tm-700" />Resumo da O.S.</h3>
      <div className="grid grid-cols-2 gap-2">
        <Numero rotulo="Total de itens" valor={r.total} sub="100%" />
        <Numero rotulo="Itens validados" valor={validados} sub={pct(validados)} tom="text-tm-700" />
        <Numero rotulo="Itens concluídos" valor={r.done} sub={pct(r.done)} tom="text-info-600" />
        <Numero rotulo="Não conformidades" valor={o.nonConformities.length + r.notDone} sub={pct(o.nonConformities.length + r.notDone)} tom={o.nonConformities.length + r.notDone ? "text-danger-600" : "text-gray-900"} />
      </div>
      {o.dueAt && (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-surface-border px-3 py-2">
          <span className="text-gray-500" aria-hidden>📅</span>
          <div>
            <p className="text-xs text-gray-500">Previsão de conclusão</p>
            <p className="font-semibold text-gray-900">{new Date(o.dueAt).toLocaleDateString("pt-BR")}
              {dias != null && o.status !== "completed" && o.status !== "cancelled" && <span className="ml-2 text-xs font-normal text-gray-400">{dias > 0 ? `Em ${dias} dia${dias === 1 ? "" : "s"}` : dias === 0 ? "Hoje" : `Atrasada ${-dias} dia${dias === -1 ? "" : "s"}`}</span>}
            </p>
          </div>
        </div>
      )}
      {t && (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            <ResumoLinha r="Componentes inspecionados" v={t.summary.inspected} />
            <ResumoLinha r="Intervenções realizadas" v={t.summary.interventions} />
            <ResumoLinha r="Pendências" v={t.summary.pending} />
            <ResumoLinha r="Críticos em aberto" v={t.summary.criticalOpen} />
          </dl>
          {pc && (
            <div>
              <p className="text-xs text-gray-500">Condição após a manutenção</p>
              <p><span className={POST_CONDITION[pc].badge}>{POST_CONDITION[pc].label}</span></p>
              <p className="mt-0.5 text-xs text-gray-400">{POST_CONDITION[pc].hint}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Numero({ rotulo, valor, sub, tom }: { rotulo: string; valor: number; sub?: string; tom?: string }) {
  return (
    <div className="rounded-lg border border-surface-border p-2.5">
      <p className="text-xs text-gray-500">{rotulo}</p>
      <p className={`text-3xl font-semibold leading-tight ${tom ?? "text-gray-900"}`}>{valor}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

/** "Observações" com contador, como na referência; "Salvar" grava no campo de observações da O.S. */
function Observacoes({ valor, editavel, salvando, alterado, onMudar, onSalvar }: {
  valor: string; editavel: boolean; salvando: boolean; alterado: boolean; onMudar: (v: string) => void; onSalvar: () => void;
}) {
  return (
    <section className="card p-4">
      <h3 className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-900"><MaintenanceIcon name="generic" className="h-5 w-5 text-tm-700" />Observações</h3>
      <textarea className="input w-full" rows={4} maxLength={500} disabled={!editavel} value={valor}
        placeholder="Digite observações sobre a execução da manutenção…" onChange={(e) => onMudar(e.target.value)} />
      <div className="mt-1 flex items-center justify-between">
        {editavel && alterado ? <button type="button" className="btn-secondary" disabled={salvando} onClick={onSalvar}>{salvando ? "Salvando…" : "Salvar rascunho"}</button> : <span />}
        <span className="text-xs text-gray-400">{valor.length}/500</span>
      </div>
    </section>
  );
}

/** "Evidências / anexos": as fotos anexadas nas inspeções da O.S. (cada foto é anexada no componente). */
function Evidencias({ o }: { o: MaintenanceWorkOrderDetailDto }) {
  const fotos = (o.technical?.inspections ?? []).flatMap((i) => i.attachments.map((a) => ({ ...a, onde: `${i.itemName}${i.positionLabel ? ` — ${i.positionLabel}` : ""}` })));
  return (
    <section className="card p-4">
      <h3 className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-900"><MaintenanceIcon name="generic" className="h-5 w-5 text-tm-700" />Evidências / anexos</h3>
      {fotos.length === 0 ? (
        <p className="rounded-lg border border-dashed border-surface-border p-4 text-center text-sm text-gray-500">
          As fotos são anexadas em cada item técnico, ao registrar o componente.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {fotos.map((f) => <EvidenceThumb key={f.id} anexo={{ id: f.id, fileName: f.fileName, mimeType: f.mimeType, caption: f.caption ?? f.onde }} />)}
        </div>
      )}
    </section>
  );
}

const ResumoLinha = ({ r, v }: { r: string; v: number }) => (
  <>
    <dt className="text-gray-500">{r}</dt>
    <dd className="text-right font-medium text-gray-900">{v}</dd>
  </>
);

/** Problemas encontrados nas inspeções da O.S. — resolvido nesta O.S., pendente ou ainda sem destino. */
function ProblemasEncontrados({ o }: { o: MaintenanceWorkOrderDetailDto }) {
  const lista = o.technical?.problems ?? [];
  if (!lista.length) return null;
  return (
    <section className="card p-4">
      <h3 className="mb-2 font-medium text-gray-900">Problemas encontrados <span className="text-sm font-normal text-gray-500">— {lista.length}</span></h3>
      <ul className="divide-y divide-gray-100">
        {lista.map((p) => (
          <li key={p.inspectionId} className={`flex flex-wrap items-start justify-between gap-2 border-l-4 py-2 pl-3 ${COND_STYLE[p.condition].ring}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">{p.itemName}{p.positionLabel ? ` — ${p.positionLabel}` : ""}</p>
              <p className="text-xs text-gray-500">
                Encontrado: {COND_LABEL[p.condition]}
                {p.state === "resolved" ? ` · agora: ${COND_LABEL[p.finalCondition]}` : ""}
                {p.reason ? ` · ${p.reason}` : ""}
                {p.owner ? ` · resp.: ${p.owner}` : ""}
                {p.dueAt ? ` · prazo ${new Date(p.dueAt).toLocaleDateString("pt-BR")}` : ""}
              </p>
            </div>
            <span className={PROBLEM_STATE[p.state].badge}>{p.state === "resolved" ? "✓ " : p.state === "pending" ? "⚠ " : ""}{PROBLEM_STATE[p.state].label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Resumo técnico gerado na conclusão, só dos dados estruturados. */
function ResumoTecnicoFinal({ o }: { o: MaintenanceWorkOrderDetailDto }) {
  const r = o.technicalReport;
  if (!r) return null;
  const pc = POST_CONDITION[r.postCondition];
  return (
    <section className="card p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium text-gray-900">Resumo técnico</h3>
        <span className={pc.badge}>{pc.label}</span>
      </div>
      <p className="text-sm text-gray-700">
        Manutenção preventiva realizada{r.odometerKm != null ? ` em ${fmtNumber(r.odometerKm)} km` : ""} · {r.counts.inspected} componente(s) inspecionado(s) ·
        {" "}{r.counts.interventions} intervenção(ões) · {r.counts.pending} pendência(s).
      </p>
      {r.interventions.length > 0 && (
        <div><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Principais intervenções</p>
          <ul className="list-disc pl-5 text-sm text-gray-800">{r.interventions.map((i) => <li key={i}>{i}</li>)}</ul></div>
      )}
      {r.pendings.length > 0 && (
        <div><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Pendências</p>
          <ul className="list-disc pl-5 text-sm text-gray-800">{r.pendings.map((i) => <li key={i}>{i}</li>)}</ul></div>
      )}
      {r.nextPreventive.length > 0 && (
        <div><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Próxima preventiva prevista</p>
          <ul className="text-sm text-gray-800">{r.nextPreventive.map((n) => (
            <li key={n.label}>{n.label}: {n.nextKm != null ? `${fmtNumber(n.nextKm)} km` : "—"}{n.nextDate ? ` · ${new Date(n.nextDate).toLocaleDateString("pt-BR")}` : ""}</li>
          ))}</ul></div>
      )}
      {r.erpOrders.length > 0 && <p className="text-sm text-gray-600">O.S. ERP #{r.erpOrders.join(", #")}</p>}
    </section>
  );
}
